import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

import { DateField } from './DateField';

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  /** Sits on the label's row, at its far end — "Forgot password?" beside "Password". */
  labelAction?: ReactNode;
}

export function Field({ label, hint, error, labelAction, id, ...input }: FieldProps) {
  const fieldId = id ?? `field-${label.replace(/\s+/g, '-').toLowerCase()}`;

  // The hint and the error are read out with the field rather than sitting silently beside it.
  // Without this a screen reader announces the label and the value and stops — so the one sentence
  // explaining why the value was rejected is the one part of the form its user never hears.
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      {labelAction ? (
        <div className="field__label-row">
          <label className="field__label" htmlFor={fieldId}>
            {label}
          </label>
          {labelAction}
        </div>
      ) : (
        <label className="field__label" htmlFor={fieldId}>
          {label}
        </label>
      )}

      {/* `type="date"` is routed to our own control rather than the browser's, so every date in
          the app is written and read the same way regardless of the visitor's OS locale. Callers
          keep passing an ISO string and getting one back, so nothing else had to change. */}
      {input.type === 'date' ? (
        <DateField
          id={fieldId}
          required={input.required}
          value={typeof input.value === 'string' ? input.value : ''}
          onChange={(iso) =>
            input.onChange?.({
              target: { value: iso },
              currentTarget: { value: iso },
            } as React.ChangeEvent<HTMLInputElement>)
          }
        />
      ) : (
        <input
          id={fieldId}
          className="field__input"
          // Announced as invalid, not merely rendered in red. Colour alone carries nothing to a
          // screen reader, and nothing at all to the part of the audience that cannot distinguish it.
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          {...input}
        />
      )}

      {hint && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function Checkbox({
  label,
  hint,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode }) {
  // useId, not Math.random: a random id changes on every render, so the label's htmlFor points at
  // a different element each time and anything referencing the field from outside — an error
  // message, a describedby — cannot hold on to it. Clicking still worked because the label wraps
  // the input, which is exactly why this survived.
  const generated = useId();
  const fieldId = input.id ?? generated;

  return (
    <div className="field">
      <label className="field__check" htmlFor={fieldId}>
        <input id={fieldId} type="checkbox" {...input} />
        <span>{label}</span>
      </label>
      {hint && <p className="field__hint">{hint}</p>}
    </div>
  );
}

export function FormError({ message }: { message?: string | null }) {
  if (!message) {
    return null;
  }

  return (
    <p className="status-error" role="alert">
      {message}
    </p>
  );
}
